import { Fragment, useCallback, useEffect, useState } from "react";
import { FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtRp, fmtDate, todayISO, downloadBlob, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const monthStart = () => todayISO().slice(0, 7) + "-01";

function SubAccountDialog({ form, onClose, onSaved }) {
  const [name, setName] = useState(form?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setName(form?.name || ""), [form]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.mode === "add") await api.post(`/accounting/accounts/${form.parent.id}/sub`, { name });
      else await api.patch(`/accounting/accounts/${form.acc.id}/name`, { name });
      toast.success(form.mode === "add" ? "Sub akun ditambahkan" : "Sub akun diubah");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="sub-account-dialog">
        <DialogHeader><DialogTitle>{form?.mode === "add" ? `Tambah Sub Akun — ${form?.parent?.name}` : "Ubah Nama Akun"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Nama {form?.mode === "add" ? "sub akun" : "akun"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="cth. HPP Ikan Tuna" required autoFocus data-testid="input-sub-account-name" />
            {form?.mode === "add" && <p className="text-xs text-slate-500 mt-1">Kode otomatis: {form.parent.code}.XX</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-sub-account-cancel">Batal</Button>
            <Button type="submit" disabled={busy || !name.trim()} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-sub-account-submit">Simpan</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function IncomeStatementTab() {
  const { isOwner } = useAuth();
  const [range, setRange] = useState({ start: monthStart(), end: todayISO() });
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => { if (range.start && range.end) api.get("/accounting/income-statement", { params: range }).then((r) => setD(r.data)); }, [range]);
  useEffect(() => { load(); }, [load]);
  const exportPdf = async () => {
    try { const r = await api.get("/accounting/export", { params: { report: "income", ...range }, responseType: "blob" }); downloadBlob(r.data, `laba_rugi_${range.start}_${range.end}.pdf`); toast.success("PDF Laba Rugi diunduh"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (acc) => {
    if (!window.confirm(`Hapus sub akun "${acc.name}"?`)) return;
    try { await api.delete(`/accounting/accounts/${acc.id}`); toast.success("Sub akun dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const IconBtn = ({ onClick, title, testId, children, danger }) => (
    <button type="button" onClick={onClick} title={title} data-testid={testId}
      className={`p-1 rounded-md text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity ${danger ? "hover:text-rose-600 hover:bg-rose-50" : "hover:text-sky-700 hover:bg-sky-50"}`}>{children}</button>
  );

  const Rows = ({ rows, tone }) => rows.map((r) => (
    <Fragment key={r.id}>
      <tr className="border-b border-dashed border-sky-100 group" data-testid={`is-row-${r.code}`}>
        <td className="px-4 py-2 text-slate-700">
          <span className="font-mono text-xs text-slate-400 mr-2">{r.code}</span>{r.name}
          {isOwner && <span className="inline-flex ml-2 align-middle">
            <IconBtn title="Tambah sub akun" testId={`btn-add-sub-${r.code}`} onClick={() => setForm({ mode: "add", parent: r })}><Plus className="h-3.5 w-3.5" /></IconBtn>
            {!r.is_system && <IconBtn title="Ubah nama" testId={`btn-edit-acc-${r.code}`} onClick={() => setForm({ mode: "edit", acc: r, name: r.name })}><Pencil className="h-3.5 w-3.5" /></IconBtn>}
          </span>}
        </td>
        <td className={`px-4 py-2 text-right font-mono ${tone} ${r.children?.length ? "font-semibold" : ""}`}>{fmtRp(r.amount)}</td>
      </tr>
      {r.children?.map((c) => (
        <tr key={c.id} className="border-b border-dashed border-sky-50 group" data-testid={`is-row-${c.code}`}>
          <td className="pl-10 pr-4 py-1.5 text-slate-600 text-[13px]">
            <span className="text-slate-300 mr-1">└</span><span className="font-mono text-xs text-slate-400 mr-2">{c.code}</span>{c.name}
            {isOwner && <span className="inline-flex ml-2 align-middle">
              <IconBtn title="Ubah nama" testId={`btn-edit-acc-${c.code}`} onClick={() => setForm({ mode: "edit", acc: c, name: c.name })}><Pencil className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Hapus sub akun" testId={`btn-del-acc-${c.code}`} danger onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
            </span>}
          </td>
          <td className={`pr-4 py-1.5 text-right font-mono text-[13px] ${tone}`}>{fmtRp(c.amount)}</td>
        </tr>
      ))}
    </Fragment>
  ));

  return (
    <div data-testid="acc-income">
      <div className="card-aqua p-4 mb-4 flex flex-wrap items-center gap-3 rise rise-1">
        <span className="text-sm text-slate-500">Periode</span>
        <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-44" data-testid="input-income-start" />
        <span className="text-slate-400">–</span>
        <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-44" data-testid="input-income-end" />
        <div className="ml-auto flex gap-2 text-xs">
          <button className="px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 font-semibold hover:bg-sky-100" onClick={() => setRange({ start: monthStart(), end: todayISO() })} data-testid="btn-income-this-month">Bulan ini</button>
          <button className="px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 font-semibold hover:bg-sky-100" onClick={() => setRange({ start: todayISO().slice(0, 4) + "-01-01", end: todayISO() })} data-testid="btn-income-this-year">Tahun ini</button>
          <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" disabled={!d} onClick={exportPdf} data-testid="btn-export-income-pdf"><FileText className="h-4 w-4 mr-1" /> Export PDF</Button>
        </div>
      </div>
      {d && (
        <div className="card-aqua p-6 max-w-3xl rise rise-2">
          <div className="text-center mb-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">PT Cahaya Fish Bali</div>
            <h3 className="text-xl font-bold text-slate-900">Laporan Laba Rugi</h3>
            <div className="text-sm text-slate-500">{fmtDate(d.start)} – {fmtDate(d.end)}</div>
          </div>
          {isOwner && <p className="text-[11px] text-slate-400 mb-2">Arahkan kursor ke nama akun untuk menambah, mengubah, atau menghapus sub akun.</p>}
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-4 py-2 rounded-lg">Pendapatan</div>
          <table className="w-full text-sm mt-1"><tbody><Rows rows={d.revenues} tone="text-emerald-700" /><tr className="font-bold"><td className="px-4 py-2.5">Total Pendapatan</td><td className="px-4 py-2.5 text-right font-mono" data-testid="is-total-revenue">{fmtRp(d.total_revenue)}</td></tr></tbody></table>
          <div className="text-xs font-bold uppercase tracking-wider text-rose-700 bg-rose-50 px-4 py-2 rounded-lg mt-5">Beban</div>
          <table className="w-full text-sm mt-1"><tbody><Rows rows={d.expenses} tone="text-rose-600" /><tr className="font-bold"><td className="px-4 py-2.5">Total Beban</td><td className="px-4 py-2.5 text-right font-mono" data-testid="is-total-expense">{fmtRp(d.total_expense)}</td></tr></tbody></table>
          <div className={`mt-6 flex justify-between items-center rounded-xl px-4 py-3 font-bold text-white ${d.net_income >= 0 ? "bg-slate-900" : "bg-rose-700"}`}>
            <span>{d.net_income >= 0 ? "LABA BERSIH" : "RUGI BERSIH"}</span>
            <span className="font-mono text-lg" data-testid="is-net-income">{fmtRp(d.net_income)}</span>
          </div>
        </div>
      )}
      <SubAccountDialog form={form} onClose={() => setForm(null)} onSaved={load} />
    </div>
  );
}
